import Vue from 'vue';
import pouch from './pouch.js';
import rCache from './reactive-cache.js';
import { checkLoginPath, collaboratorsPath, couchFetchOverride, loginPath, logoutPath, postToEndpoint, redirectToLogin } from './backend-api.js';

const kSessionStatusKey = 'session';
export const kAnonUsername = '$anonymous'
const kAnonCouchname = '_anonymous'

function readOfflineStatus() {
  try {
    return JSON.parse(localStorage.getItem(kSessionStatusKey)) || {};
  } catch (e) {
    return {};
  }
}

function writeOfflineStatus(status = {}) {
  return localStorage.setItem(kSessionStatusKey, JSON.stringify(status));
}

async function readOnlineStatus() {
  try {
    const check_login = await postToEndpoint(checkLoginPath);
    const collaborators = await postToEndpoint(collaboratorsPath);
    const result = {
      online: true,
      login: check_login.login,
      couches: check_login.couches,
      collaborators,
    }
    return result;
  } catch (e) {
    // TODO: Special handling for HTTP 403/CSRF failure.
    return {
      online: false,
    };
  }
}

async function getSessionStatusSummary() {
  performance.mark('start')
  const {
    login: offlineLogin,
    couches: offlineCouches,
    collaborators: offlineCollaborators,
  } = readOfflineStatus();
  const {
    online,
    login: onlineLogin,
    couches: onlineCouches,
    collaborators: onlineCollaborators,
  } = await readOnlineStatus();
  performance.mark('end')
  performance.measure('3. getSessionStatusSummary', 'start', 'end')

  if (online) {
    if (!onlineLogin) {
      return {
        loggedInAs: null,
        couchNames: onlineCouches,
        collaborators: onlineCollaborators,
      };
    }
    return {
      loggedInAs: onlineLogin,
      couchNames: onlineCouches,
      collaborators: onlineCollaborators,
    };
  }
  return {
    loggedInAs: offlineLogin || null,
    couchNames: offlineCouches,
    collaborators: offlineCollaborators,
  };
}

function getAnonymousStatus() {
  const cookieRegex = /(?:^|;)\s*anonymous_session=True(?:;|$)/;
  const cookieMatch = cookieRegex.exec(document.cookie);
  return !!cookieMatch
}

export async function restoreOrInitSession() {
  const {
    loggedInAs,
    couchNames,
    collaborators,
  } = await getSessionStatusSummary();
  const useAnonymousSession = getAnonymousStatus();

  if (loggedInAs && useAnonymousSession) {
    loadFromAnonymousDB(couchNames[0])
  }
  if (loggedInAs) {

    document.getElementById('app').textContent = `Loading your data...`;

    writeOfflineStatus({
      login: loggedInAs,
      couches: couchNames,
      collaborators: collaborators,
    });
    const sessionState = await initializePouch(loggedInAs, couchNames);
    sessionState.loggedIn = true;
    sessionState.loggedInAs = loggedInAs;
    for (const dbName of Object.keys(collaborators)) {
      sessionState.dbs[dbName].collaborators = collaborators[dbName];
    }
    return sessionState;
  }
  if (useAnonymousSession) {
    return await initAnonymousSession();
  }
  return {
    loggedIn: false,
  }
}

export async function initAnonymousSession() {
  writeOfflineStatus({
    login: kAnonUsername,
    couches: [kAnonCouchname],
    collaborators: { [kAnonCouchname]: [kAnonUsername] },
  });

  const localDB = pouch.createLocalDB(kAnonCouchname)
  localDB.collaborators = [kAnonUsername];
  await rCache.init(localDB)

  return {
    dbs: { [localDB.name]: localDB },
    defaultDB: localDB,
    reactiveCache: rCache,
    syncStatus: { activity: "offline" },
    loggedIn: true,
    loggedInAs: kAnonUsername,
  };
}

export const sessionOnlyForTesting = {
  getSessionStatusSummary,
};

async function createPouch(couchName) {
  let remoteDB;
  try {
    remoteDB = pouch.createRemoteDB(couchName, { fetch: couchFetchOverride });
  } catch (err) {
    console.error('Failed to open the remote database:', err);
  }
  const localDB = pouch.createLocalDB(couchName);

  performance.mark('start')
  await new Promise((resolve) => {
    // We want to wait in order to avoid presenting stale data. However, it's
    // not necessarily that valuable.
    localDB.replicate.from(remoteDB)
      .on('complete', resolve)
      .on('error', resolve);
  });
  performance.mark('end')
  performance.measure('4.1. replication: ' + couchName, 'start', 'end')
  return { localDB, remoteDB }
}

export async function loadFromAnonymousDB(targetCouchName) {
  const anonymousDB = pouch.createLocalDB(kAnonCouchname)
  const targetDB = pouch.createLocalDB(targetCouchName)

  await new Promise((resolve) => {
    targetDB.replicate.from(anonymousDB)
      .on('complete', resolve)
      .on('error', resolve);
  });
  document.cookie = 'anonymous_session=uploaded; path=/';
}


function syncPouches(dbs) {
  const combinedStatus = {}
  for (const db of dbs) {
    const { localDB, remoteDB } = db
    combinedStatus[localDB.name] = pouch.setupSync(localDB, remoteDB)
  }

  Object.defineProperty(combinedStatus, 'activity', {
    get() {
      for (const status of Object.values(combinedStatus)) {
        if (status.uploadActivity == 'offline' ||
          status.downloadActivity == 'offline') {
          return 'offline';
        }
        if (status.uploadActivity == 'push') {
          return 'push';
        }
        if (status.downloadActivity == 'fetch') {
          return 'fetch';
        }
      }
      return 'idle';
    },
  });
  return combinedStatus;
}

// NOTE: should be ran after createReactiveCache which has primaryDB detection
function assembleDBinfo(dbList, login) {
  let defaultDB = null;
  const dbDict = {};

  for (const db of dbList) {
    dbDict[db.name] = db;
    if (db.primary) {
      if (defaultDB) {
        console.error(
          'Already found primary DB:', defaultDB.name,
          'Cannot have another one!', db.name,
        )
      } else {
        defaultDB = db;
      }
    }
  }
  // Fall back to old detection mechanism based on db name.
  if (!defaultDB) {
    for (const db of dbList) {
      if (db.name == login || db.name.includes('_default')) {
        defaultDB = db;
        break;
      }
    }
  }
  if (!defaultDB) {
    const errorText = "Cannot find user's default couchDB!"
    console.error(errorText)
    alert(errorText)
  }
  return { dbDict, defaultDB };
}

async function initializePouch(login, couchNames) {
  console.debug(performance.now().toFixed() + ': initializePouch() start');
  performance.mark('start-pouches')
  const pouchPairs = await Promise.all(
    couchNames.map((dbName) => createPouch(dbName))
  );
  const localPouches = pouchPairs.map(({ localDB, remoteDB }) => localDB)
  performance.mark('pouches-ready')
  performance.measure('4. creating pouches', 'start-pouches', 'pouches-ready')

  const syncStatus = syncPouches(pouchPairs)
  const { dbDict: dbs, defaultDB } = assembleDBinfo(localPouches, login);
  
  console.debug(performance.now().toFixed() + ': createReactiveCache() start');
  await rCache.init(defaultDB, Object.values(dbs))
  performance.mark('cache-ready')
  performance.measure('5. creating reactive cache', 'pouches-ready', 'cache-ready')
  return {
    dbs,
    defaultDB,
    reactiveCache: rCache,
    syncStatus,
  };
}

// Usage:
// Vue.use(SessionPlugin); // Install plugin.
// Vue.initSession(); // Actually initialize the session.
export const SessionPlugin = {
  async install(Vue, _options) {
    // Originally, I wanted to simply use Vue.observable and a simple object,
    // but it turns out that it doesn't give the same reactivity as using
    // data attributes on a Vue instance, so I create a full-fledged Vue
    // instance to store the reactive values.
    const reactiveContainer = new Vue({
      data: {
        sessionState: null,
      },
    });
    // I want to make sure the getters are as simple as possible, but also that
    // they explicitly refer to fields on reactiveContainer, so I define
    // them all explicitly and with literal names. The property names are set
    // once, so I have no issue generating them (i.e. using the prefix var).
    for (const [obj, prefix] of [[Vue, ''], [Vue.prototype, '$']]) {
      Object.defineProperties(obj, {
        [prefix + 'sessionState']: {
          get() {
            return reactiveContainer.sessionState;
          },
        },
        [prefix + 'defaultDB']: {
          get() {
            return reactiveContainer.sessionState.defaultDB;
          },
        },
        [prefix + 'reactiveCache']: {
          get() {
            return reactiveContainer.sessionState.reactiveCache;
          },
        },
      });
    }


    Vue.logout = () => endSession(reactiveContainer);
    Vue.initSession = async () => {

      console.group('restoreOrInitSession')
      console.debug(performance.now().toFixed() + ': restoreOrInitSession() start');
      reactiveContainer.sessionState = await restoreOrInitSession();
      console.debug(performance.now().toFixed() + ': restoreOrInitSession() end');
      console.groupEnd()

      if (!reactiveContainer.sessionState.loggedIn) {
        redirectToLogin('You have to log in.');
      }

      performance.mark('initSession() end')
    };
  },
};

async function endSession(reactiveContainer) {
  if (!reactiveContainer.sessionState) {
    return;
  }
  const logoutPromise = postToEndpoint(logoutPath);
  try {
    await Promise.all(
      Object.values(reactiveContainer.sessionState.dbs).map(
        pouch => pouch.destroy()
      )
    );
  } catch (err) {
    console.error('Failed to destroy the local database:', err);
  }
  try {
    await logoutPromise;
  } catch (err) {
    console.error('Logout request failed:', err);
    // The local database is destroyed at this stage, we need to  refresh
    // or rebuild the sessionState.
    reactiveContainer.sessionState = await restoreOrInitSession();
    return;
  }
  window.location.href = loginPath;
}
